"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ChevronLeft, ArrowRight, Crown } from "lucide-react";
import Link from "next/link";

export default function VipTableMapHero() {
  const { t } = useLang();

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-[#0A0814] text-white">
      {/* Video — base layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      >
        <source src="/videos/feature-vip-table-map.mp4" type="video/mp4" />
      </video>
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-[#0A0814]/75 pointer-events-none" />
      <div
        className="absolute top-1/4 -left-32 w-[800px] h-[800px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.30) 0%, transparent 60%)" }}
      />
      <div
        className="absolute bottom-0 -right-32 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,182,0.20) 0%, transparent 60%)" }}
      />
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-white/55 hover:text-white transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center inline-flex items-center gap-2 justify-center w-full"
        >
          <Crown className="w-3 h-3" style={{ color: "#F472B6" }} />
          <span style={{ color: "#F472B6" }}>
            {t("VIP Table Map · Live floor", "VIP Table Map · Floor en vivo")}
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[44px] sm:text-6xl md:text-[88px] lg:text-[110px] font-black leading-[0.95] tracking-[-0.04em] mb-6"
        >
          {t("Your floor.", "Tu floor.")}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #C4B5FD 0%, #F472B6 50%, #FB923C 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("Live. Everywhere.", "En vivo. En todos lados.")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-white/60 max-w-[680px] mx-auto leading-relaxed mb-8"
        >
          {t(
            "Every host, every bartender, every supervisor sees the same room — in real time. VIP-2 just hit minimum? Everyone knows. The table closed at 11:30? The door host already knows.",
            "Cada host, cada bartender, cada supervisor ve el mismo salón — en tiempo real. ¿VIP-2 bajó del mínimo? Todos lo saben. ¿Cerró la mesa a las 11:30? El host de la puerta ya lo sabe."
          )}
        </motion.p>

        {/* Stat ribbon */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="flex flex-wrap items-center justify-center gap-4 mb-12"
        >
          <Stat label={t("Table states", "Estados de mesa")} value="4" sub={t("open · reserved · occupied · closed", "abierta · reservada · ocupada · cerrada")} />
          <Divider />
          <Stat label={t("Bottle service", "Bottle service")} value={t("2 taps", "2 taps")} sub={t("from preset menus", "desde menús preset")} highlight />
          <Divider />
          <Stat label={t("Updates", "Updates")} value={t("Live", "En vivo")} sub={t("across every screen", "en cada pantalla")} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="text-center"
        >
          <Link
            href="/signup?vertical=nightlife"
            className="group inline-flex items-center gap-2 text-[15px] font-bold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform shadow-2xl text-white"
            style={{
              background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
              boxShadow: "0 20px 60px rgba(124, 58, 237, 0.5)",
            }}
          >
            {t("Draw your floor →", "Dibuja tu floor →")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="text-center mt-6 text-[12px] font-medium text-white/35"
        >
          {t("Scroll down to see it live ↓", "Sigue bajando para verlo en vivo ↓")}
        </motion.p>
      </div>
    </section>
  );
}

function Stat({
  label,
  value,
  sub,
  highlight,
}: {
  label: string;
  value: string;
  sub: string;
  highlight?: boolean;
}) {
  return (
    <div className="text-center">
      <div className="text-[9px] font-bold uppercase tracking-widest text-white/40 mb-1">
        {label}
      </div>
      <div
        className="text-[32px] md:text-[40px] font-black leading-none tabular-nums"
        style={
          highlight
            ? {
                background: "linear-gradient(135deg, #C4B5FD 0%, #F472B6 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }
            : { color: "white" }
        }
      >
        {value}
      </div>
      <div className="text-[10px] font-medium text-white/50 mt-1">{sub}</div>
    </div>
  );
}

function Divider() {
  return <div className="w-px h-12 bg-white/10 hidden sm:block" />;
}
